// AuthCallback — route /auth/callback (public). Phase 4 / SSO (D10).
//
// OAuth Authorization-Code + PKCE callback for the single configured OIDC IdP.
// Login.tsx (sibling-owned) redirects to the Cognito Hosted UI /authorize with a
// PKCE code_challenge (S256), an unguessable `state`, and a `nonce`, persisting
// the verifier/state/nonce in sessionStorage under the keys below. This page:
//   1. verifies `state` against the stored value (CSRF / login-CSRF),
//   2. exchanges the code at the Hosted-UI /oauth2/token endpoint with the PKCE
//      code_verifier (public client — no secret),
//   3. verifies the ID token's `nonce` (replay binding),
//   4. stores the tokens and hard-navigates home so AuthProvider bootstraps.
//
// The whole page is guarded behind INNOVATEFLOW_CONFIG.ENABLE_SSO — with SSO off
// (or no Cognito domain configured) it no-ops and redirects to /login.
// iflow palette; light theme only.
//
// sessionStorage contract with Login.tsx (Phase 4):
//   iflow_oauth_state    — the CSRF state param
//   iflow_oauth_nonce    — the ID-token nonce
//   iflow_pkce_verifier  — the PKCE code_verifier
import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Loader2, AlertTriangle, Check } from 'lucide-react'
import { STORAGE_KEYS } from '../../constants/config'

const SS_STATE = 'iflow_oauth_state'
const SS_NONCE = 'iflow_oauth_nonce'
const SS_VERIFIER = 'iflow_pkce_verifier'

interface SsoRuntimeConfig {
  ENABLE_SSO?: boolean
  COGNITO_DOMAIN?: string
  SSO_PROVIDER_NAME?: string
  REGION: string
  COGNITO_CLIENT_ID: string
}
function ssoConfig(): SsoRuntimeConfig | null {
  if (typeof window === 'undefined' || !window.INNOVATEFLOW_CONFIG) return null
  return window.INNOVATEFLOW_CONFIG as unknown as SsoRuntimeConfig
}

/** Build the Hosted-UI token host. COGNITO_DOMAIN may be a bare prefix
 *  (`my-app`) or an already-qualified host — handle both. */
function tokenHost(domain: string, region: string): string {
  return domain.includes('.') ? domain : `${domain}.auth.${region}.amazoncognito.com`
}

function decodeJwtNonce(idToken: string): string | undefined {
  try {
    const part = idToken.split('.')[1]
    if (!part) return undefined
    let b64 = part.replace(/-/g, '+').replace(/_/g, '/')
    while (b64.length % 4) b64 += '='
    const payload = JSON.parse(atob(b64)) as { nonce?: string }
    return payload.nonce
  } catch {
    return undefined
  }
}

export default function AuthCallback() {
  const [params] = useSearchParams()
  const [error, setError] = useState('')
  const cfg = ssoConfig()
  const providerName = cfg?.SSO_PROVIDER_NAME || 'your organisation'
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true

    // SSO disabled or unconfigured → this page is a no-op; go to /login.
    if (!cfg || cfg.ENABLE_SSO !== true || !cfg.COGNITO_DOMAIN) {
      window.location.replace('/login')
      return
    }

    const err = params.get('error_description') || params.get('error')
    if (err) { setError(err); return }

    const code = params.get('code')
    const returnedState = params.get('state')
    if (!code) { setError('The identity provider did not return an authorization code.'); return }

    const expectedState = sessionStorage.getItem(SS_STATE)
    const verifier = sessionStorage.getItem(SS_VERIFIER)
    const expectedNonce = sessionStorage.getItem(SS_NONCE)

    if (!expectedState || returnedState !== expectedState) {
      setError('Sign-in could not be verified (state mismatch). Please start again from the sign-in page.')
      return
    }
    if (!verifier) {
      setError('This sign-in session has expired. Please start again from the sign-in page.')
      return
    }

    const host = tokenHost(cfg.COGNITO_DOMAIN, cfg.REGION)
    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: cfg.COGNITO_CLIENT_ID,
      code,
      redirect_uri: `${window.location.origin}/auth/callback`,
      code_verifier: verifier,
    })

    fetch(`https://${host}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    })
      .then(async (resp) => {
        const data = await resp.json().catch(() => ({}))
        if (!resp.ok || !data.id_token) {
          throw new Error(data.error_description || data.error || 'Token exchange failed.')
        }
        // Replay binding: the ID token's nonce must match what we sent.
        if (expectedNonce) {
          const got = decodeJwtNonce(data.id_token as string)
          if (got !== expectedNonce) throw new Error('Sign-in could not be verified (nonce mismatch).')
        }
        localStorage.setItem(STORAGE_KEYS.ID_TOKEN, data.id_token)
        if (data.access_token) localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.access_token)
        if (data.refresh_token) localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.refresh_token)
        sessionStorage.removeItem(SS_STATE)
        sessionStorage.removeItem(SS_NONCE)
        sessionStorage.removeItem(SS_VERIFIER)
        // Hard navigation so AuthProvider re-initialises from the stored tokens.
        window.location.replace('/')
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Sign-in failed. Please try again.')
      })
  }, [cfg, params])

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-sky-50 via-white to-[#E8EFF4]">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl border border-[rgba(12,74,110,0.12)] p-8 shadow-xl text-center">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-iflow-500 to-iflow-700 mx-auto mb-3 flex items-center justify-center text-white text-xl font-bold">iF</div>

          {error ? (
            <>
              <div className="w-12 h-12 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center mx-auto mb-3"><AlertTriangle size={22} /></div>
              <h2 className="text-xl font-bold text-iflow-900 mb-1">Sign-in couldn&apos;t complete</h2>
              <p className="text-sm text-slate-600">{error}</p>
              <a href="/login" className="inline-block mt-5 py-2.5 px-5 bg-iflow-600 text-white text-sm font-semibold rounded-lg hover:bg-iflow-700">Back to sign in</a>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-iflow-900 mb-1">Signing you in…</h2>
              <p className="text-sm text-slate-500 mb-6">via {providerName} single sign-on</p>
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-left space-y-2.5">
                <div className="flex items-center gap-2 text-sm text-emerald-800"><span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs shrink-0"><Check size={12} /></span> Identity verified by {providerName}</div>
                <div className="flex items-center gap-2 text-sm text-emerald-800"><span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs shrink-0"><Check size={12} /></span> Authentication handled by your organisation</div>
                <div className="flex items-center gap-2 text-sm text-emerald-800"><span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs shrink-0"><Check size={12} /></span> No additional code required</div>
              </div>
              <div className="mt-5 flex items-center justify-center gap-2 text-xs text-slate-400"><Loader2 size={14} className="animate-spin" /> Redirecting to your workspace…</div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
